/**
 * Entrypoint do PWA da Plataforma (Task 10).
 *
 * Único lugar do app que lê ambiente: monta o cliente de API, o provedor de
 * sessão (Supabase real quando há credencial; simulador quando não há) e o
 * cache de queries, e injeta tudo no `<App />`. Nenhum segredo aparece aqui —
 * só nomes de variáveis de build.
 */
import { createElement as h } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient } from "@tanstack/react-query";
import { App } from "./App.js";
import { PlatformApiClient, resolvePlatformApiBase } from "./api/platform-client.js";
import { createStaffSessionProvider } from "./auth/session.js";
import { registerServiceWorker } from "./pwa/register.js";
// O CSS entra pelo <link> do index.html (e não por import TS) para que o
// typecheck da raiz, que não conhece asset de bundler, continue válido.

// `DEV`/`MODE` entram na escolha do provedor de sessão: sem credencial, só
// dev/teste recebe simulador — produção mal configurada nega (D-10.22).
const env = import.meta.env;
const sessionProvider = createStaffSessionProvider(env);

const client = new PlatformApiClient({
  baseUrl: resolvePlatformApiBase(env),
  // Lido do provedor a cada request (e não capturado uma vez no boot), para
  // que login e renovação de token passem a valer sem recarregar a página.
  getAccessToken: () => sessionProvider.currentAccessToken()
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dados de operação: uma falha de rede não deve limpar a tela, e um
      // erro real precisa aparecer em vez de ficar em retry infinito.
      retry: 1,
      refetchOnWindowFocus: true
    }
  }
});

const container = document.getElementById("root");
if (!container) throw new Error("root_container_not_found");

createRoot(container).render(h(App, { client, sessionProvider, queryClient }));

registerServiceWorker();
